import { getResourceType, getResourceTypeLabel, pluralizeResourceType } from '.';

import type { ICollection, ICourse, ICoursePathProgress } from '@academy/graphql/types';
import type { IUserCourseData } from '@academy/skilljar/types';

const getUserCollectionMeta = (
  { coursePathProgressEntry }: { coursePathProgressEntry: ICoursePathProgress; },
) => {
  const completedCourses = coursePathProgressEntry?.completed_course_count ?? 0;
  const totalCourses = coursePathProgressEntry?.total_course_count ?? 0;
  const courseProgress = totalCourses > 0 ? (completedCourses / totalCourses) : 0;
  const ctaText = courseProgress > 0 ? 'Continue' : 'Get started';

  return {
    courseProgress,
    completedCourses,
    totalCourses,
    ctaText,
  };
};

const courseIsComplete = (courseId: string, userCourseData: IUserCourseData | null) => {
  if (!userCourseData) {
    return false;
  }

  const matchingCourse: any = userCourseData?.course_progress.find(
    (course: any) => course.third_party_id === courseId,
  );

  if (matchingCourse && matchingCourse.completed_at !== null) {
    return true;
  }

  return false;
};

export const sortCourseParts = (courses: ICourse[]) => {
  return [
    ...courses,
  ].sort((a, b) =>
    extractCoursePartFromDescription(a.previewText || '') -
    extractCoursePartFromDescription(b.previewText || ''));
};

const extractCoursePartFromDescription = (description: string) => {
  const partMatch = description.match(/\W(\d+)\/\d+/);
  return partMatch ? Number(partMatch[1]) : 0;
};

export const getCollectionMeta = ({ collection }: {
  collection: ICollection;
}) => {
  const resourceType = getResourceType(collection.tags);
  const pluralizedResourceType = pluralizeResourceType(resourceType);
  const resourceTypeLabel = getResourceTypeLabel(resourceType);
  const firstCourse = collection.courses[0];

  return {
    resourceType,
    resourceTypeLabel,
    pluralizedResourceType,
    firstCourse,
    title: collection.title,
    registrationUrl: collection.registrationUrl,
    resourceId: collection.thirdPartyId,
  };
};

const initialCoursePathProgress = {
  completed_at: '', completed_course_count: 0, total_course_count: 0, third_party_id: '',
};

export const getUserProgress = (
  { userCourseData, collection }: { userCourseData: IUserCourseData | null; collection: ICollection; },
) => {

  const { firstCourse } = getCollectionMeta({ collection });

  const firstIncompleteCourse = userCourseData?.course_progress?.length
    ? sortCourseParts(collection.courses)
      .find((course: ICourse) =>
        courseIsComplete(course.thirdPartyId, userCourseData) === false)
    : undefined;

  const firstIncompleteCourseSlug = firstIncompleteCourse ? firstIncompleteCourse.slug : firstCourse.slug;

  const firstIncompleteCourseUrl = `/en-us/collections/${collection.slug}/${firstIncompleteCourseSlug}`;

  const coursePathProgressEntry =
    Array.isArray(userCourseData?.course_path_progress) ? userCourseData.course_path_progress.find((path: {
      third_party_id: string;
    }) => path.third_party_id === collection.thirdPartyId) ?? initialCoursePathProgress : initialCoursePathProgress;

  const {
    courseProgress,
    completedCourses,
    totalCourses,
    ctaText,
  } = getUserCollectionMeta({ coursePathProgressEntry });

  return {
    courseProgress,
    completedCourses,
    totalCourses,
    ctaText,
    ctaLink: firstIncompleteCourseUrl,
    coursePathProgressEntry,
    firstIncompleteCourse,
    firstIncompleteCourseUrl,
  };
};
